import { Card, CardContent } from "../ui/card"
import { AlertCircle, Box, TrendingDown, TrendingUp } from "lucide-react"
import { ClientLevelItem, CategoryLevelItem } from "../../interfaces/dashboards/PerformanceDashboard.interface"
import PDS from "../../services/dashboards/PerformanceDashboard.service"

interface ClassStockItem {
  inventory: number;
  plannedInventory: number;
  className: string;
}

interface StockDescriptionProps {
  stockLevel: number;
  topTwoClasses: Array<ClassStockItem>;
}

function ClassStockLevelDescription({ topTwoClasses, stockLevel }: StockDescriptionProps) {
  const overOrUnderstock = stockLevel > 0 ? "Overstock" : "Understock";
  const color = stockLevel < 0 ? "text-yellow-500" : "text-red-500";
  const firstClass = topTwoClasses[0];
  const secondClass = topTwoClasses.length > 1 ? topTwoClasses[1] : null;

  stockLevel = Math.abs(stockLevel);

  return (
    <span>
      <span className={`text-sm ${color}`}>
        <strong>{PDS.moneyFormat(stockLevel)} {overOrUnderstock}</strong>
      </span>
      {" "} mainly in {firstClass.className}{secondClass ? " and " + secondClass.className : ""}
    </span>
  );
}

interface ExecutiveSummaryProps {
  clientLevelData: ClientLevelItem[];
  categoryLevelData: CategoryLevelItem[];
  epochMonth: number;
  compareEpochMonth: number;
  missedSales: number;
  totalOverstocked: number;
}

export function ExecutiveSummary({ clientLevelData, categoryLevelData, epochMonth, compareEpochMonth, missedSales, totalOverstocked }: ExecutiveSummaryProps) {
  missedSales = Math.abs(missedSales);
  const thisMonthClientLevelData = clientLevelData.find((item) => item.epochMonthSpan === epochMonth);
  const compareClientLevelData = clientLevelData.find((item) => item.epochMonthSpan === compareEpochMonth);

  // Sales Executive Plan Calculations
  const thisMonthSales = thisMonthClientLevelData?.salesRm ?? 0;
  const trend = thisMonthClientLevelData?.trend ?? 0;
  const thisMonthTrendedSales = thisMonthSales / trend;

  // Inventory Executive Plan Calculations
  const inventoryBomPlan = thisMonthClientLevelData?.inventoryBOMPlan ?? 0;
  const inventoryBomRm = thisMonthClientLevelData?.inventoryBOMRM ?? 0;
  let topTwoUnderstockedClasses: ClassStockItem[] = [];
  let topTwoOverstockedClasses: ClassStockItem[] = [];
  let totalOverstock = totalOverstocked;
  let totalUnderstock = 0;

  if (categoryLevelData.length > 0) {
    const filteredData = categoryLevelData.filter((item) => item.epochMonthRM === epochMonth);

    // Calculate total understock
    totalUnderstock = filteredData
      .filter((item) => item.inventoryBomRm < item.inventoryBomPlan)
      .reduce((sum, item) => sum + (item.inventoryBomRm - item.inventoryBomPlan), 0);

    // Get top two understocked classes
    topTwoUnderstockedClasses = filteredData
      .filter((item) => item.inventoryBomRm < item.inventoryBomPlan)
      .sort((a, b) => (a.inventoryBomRm - a.inventoryBomPlan) - (b.inventoryBomRm - b.inventoryBomPlan))
      .slice(0, 2)
      .map((item) => ({
        inventory: item.inventoryBomRm,
        plannedInventory: item.inventoryBomPlan,
        className: item.className,
      }));

    // Get top two overstocked classes
    topTwoOverstockedClasses = filteredData
      .filter((item) => item.inventoryBomRm > item.inventoryBomPlan)
      .sort((a, b) => (b.inventoryBomRm - b.inventoryBomPlan) - (a.inventoryBomRm - a.inventoryBomPlan))
      .slice(0, 2)
      .map((item) => ({
        inventory: item.inventoryBomRm,
        plannedInventory: item.inventoryBomPlan,
        className: item.className,
      }));
  }

  const compareMonthSales = compareClientLevelData?.salesRm ?? 0;
  const percentSalesChangeFromCompare = compareMonthSales && thisMonthTrendedSales ? PDS.percentageFormat(Math.abs(thisMonthTrendedSales / compareMonthSales - 1), true) : "0%";
  const markdowns = thisMonthClientLevelData?.markdownsRm ?? 0;

  const markdownSummary = markdowns > 0 ? 
    <>
      <span>
        , but markdowns at <span className="font-bold">{PDS.moneyFormat(markdowns, true)}</span> are impacting profitability.
      </span>
    </>
    : <span>.</span>;

  function OverstockUnderstockClassesOverview() {
    if (topTwoOverstockedClasses.length > 0 && topTwoUnderstockedClasses.length > 0) {
      return <span>
          {" "} with <ClassStockLevelDescription topTwoClasses={topTwoOverstockedClasses} stockLevel={totalOverstock} />
          {" "} and <ClassStockLevelDescription topTwoClasses={topTwoUnderstockedClasses} stockLevel={totalUnderstock} />.
        </span>
    } else if (topTwoOverstockedClasses.length > 0 && topTwoUnderstockedClasses.length == 0) {
      return <span>
          {" "} with <ClassStockLevelDescription topTwoClasses={topTwoOverstockedClasses} stockLevel={totalOverstock} />.
        </span>
    } else if (topTwoOverstockedClasses.length == 0 && topTwoUnderstockedClasses.length > 0) {
      return <span>
          {" "} with <ClassStockLevelDescription topTwoClasses={topTwoUnderstockedClasses} stockLevel={totalUnderstock} />.
        </span>
    }
    return <span>.</span>;
  }

  return (
    <Card className="bg-white rounded-xl">
      <CardContent className="p-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-center">
          <span className="text-2xl font-bold my-auto">Executive Summary</span>
          <AlertCircle className="h-6 w-6  m-1" />
        </div>

        <div>
          {thisMonthTrendedSales > compareMonthSales ? (
            <div className="flex items-center text-sm space-x-2">
              <TrendingUp className="inline-block mr-0.5 flex-shrink-0 self-center text-emerald-500" />
              <span className="leading-relaxed my-auto">
                Sales this month are <span className="font-bold">{PDS.moneyFormat(thisMonthSales, true)}</span>, up {percentSalesChangeFromCompare} from compare month{markdownSummary}
              </span>
            </div>
          ) : (
            <div className="flex items-center text-sm space-x-2">
              <TrendingDown className="inline-block mr-0.5 flex-shrink-0 self-center text-red-500" />
              <span className="leading-relaxed my-auto">
                Sales this month are <span className="font-bold">{PDS.moneyFormat(thisMonthSales, true)}</span>, down {percentSalesChangeFromCompare} from compare month{markdownSummary}
              </span>
            </div>
          )}
        </div>

        <div className="text-sm flex items-start">
          <Box className="inline-block mr-0.5 flex-shrink-0 self-center" />
          <div className="my-auto ml-2">
            <span>
              Current inventory levels are at <span className="font-bold">{PDS.moneyFormat(inventoryBomRm, true)}</span>
            </span>
            <OverstockUnderstockClassesOverview />
          </div>
        </div>

        {missedSales > 0 && (
          <div className="flex items-center">
            <AlertCircle className="inline-block mr-0.5 text-red-500 flex-shrink-0" />
            <p className="text-sm leading-tight my-auto ml-2">
              Understocking issues have led to approximately <span className="font-bold text-red-500">{PDS.moneyFormat(missedSales, true)}</span> in missed sales opportunity, requiring immediate attention.
            </p>
          </div>
        )}
      </div>
      </CardContent>
    </Card>
  )
}

